import { throwUa } from "./errors";
import { supabase } from "./supabase";
import { PartnerProfile } from "../models/account";
import { Project, ProjectStatus, ProjectStageValue } from "../models/project";

export interface PartnerWithTotals extends PartnerProfile {
  project_count: number;
  bonus_total: number;
  telegram_username: string | null;
}

export const fetchPartners = async (): Promise<PartnerWithTotals[]> => {
  const { data, error } = await supabase
    .from("profiles")
    .select(
      `*,
       projects!projects_owner_id_fkey(count),
       telegram_links(telegram_username)`
    )
    .eq("role", "partner");
  if (error || !data) throwUa(error);

  const bonusByUser = await fetchBonusTotals();
  return (data ?? []).map((row: any) => ({
    ...row,
    project_count: row.projects?.[0]?.count ?? 0,
    bonus_total: bonusByUser.get(row.id) ?? 0,
    telegram_username: row.telegram_links?.telegram_username ?? null,
  })) as PartnerWithTotals[];
};

const fetchBonusTotals = async (): Promise<Map<string, number>> => {
  const { data, error } = await supabase
    .from("projects")
    .select("owner_id, bonus_amount");
  if (error || !data) throwUa(error);
  const map = new Map<string, number>();
  for (const row of (data ?? []) as { owner_id: string; bonus_amount: number | null }[]) {
    map.set(row.owner_id, (map.get(row.owner_id) ?? 0) + (row.bonus_amount ?? 0));
  }
  return map;
};

export const fetchPartner = async (
  id: string
): Promise<(PartnerProfile & { telegram_username: string | null }) | null> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*, telegram_links(telegram_username)")
    .eq("id", id)
    .maybeSingle<any>();
  if (error || !data) throwUa(error);
  if (!data) return null;
  return {
    ...data,
    telegram_username: data.telegram_links?.telegram_username ?? null,
  };
};

export const fetchPartnerProjects = async (ownerId: string): Promise<Project[]> => {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("owner_id", ownerId)
    .order("submitted_at", { ascending: false });
  if (error || !data) throwUa(error);
  return (data ?? []) as Project[];
};

export const fetchAllProjects = async (
  limit?: number
): Promise<(Project & { owner_name: string | null })[]> => {
  let query = supabase
    .from("projects")
    .select("*, profiles!projects_owner_id_fkey(name)")
    .order("submitted_at", { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error } = await query;
  if (error || !data) throwUa(error);
  return (data ?? []).map((row: any) => ({
    ...row,
    owner_name: row.profiles?.name ?? null,
  })) as (Project & { owner_name: string | null })[];
};

export const fetchProject = async (id: string): Promise<Project | null> => {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("id", id)
    .maybeSingle<Project>();
  if (error || !data) throwUa(error);
  return data;
};

export const fetchProjectStageValues = async (
  projectId: string
): Promise<ProjectStageValue[]> => {
  const { data, error } = await supabase
    .from("project_status_values")
    .select("*")
    .eq("project_id", projectId)
    .order("id", { ascending: true });
  if (error) throwUa(error);
  return (data ?? []) as ProjectStageValue[];
};

export interface ProjectStageValueInput {
  status: ProjectStatus;
  contract_amount: number | null;
  bonus_amount: number | null;
  manager_comment: string | null;
}

export const createProjectStageValue = async (
  projectId: string,
  values: ProjectStageValueInput
): Promise<ProjectStageValue> => {
  const { data, error } = await supabase
    .from("project_status_values")
    .insert({ project_id: projectId, ...values })
    .select("*")
    .single<ProjectStageValue>();
  if (error || !data) throwUa(error);
  return data as ProjectStageValue;
};

export const updateProjectStageValue = async (
  id: number,
  values: ProjectStageValueInput
): Promise<ProjectStageValue> => {
  const { data, error } = await supabase
    .from("project_status_values")
    .update(values)
    .eq("id", id)
    .select("*")
    .single<ProjectStageValue>();
  if (error || !data) throwUa(error);
  return data as ProjectStageValue;
};

export const deleteProjectStageValue = async (id: number): Promise<void> => {
  const { error } = await supabase.from("project_status_values").delete().eq("id", id);
  if (error) throwUa(error);
};

export interface AdminProjectUpdate {
  name?: string;
  client_name?: string | null;
  client_phone?: string | null;
  address?: string | null;
  area_m2?: number | null;
  status?: ProjectStatus;
  contract_amount?: number | null;
  bonus_amount?: number | null;
  manager_comment?: string | null;
}

export const updateProject = async (
  id: string,
  update: AdminProjectUpdate
): Promise<Project> => {
  const { data, error } = await supabase
    .from("projects")
    .update(update)
    .eq("id", id)
    .select("*")
    .single<Project>();
  if (error || !data) throwUa(error);
  return data as Project;
};

export const deleteProject = async (id: string): Promise<void> => {
  const { error } = await supabase.from("projects").delete().eq("id", id);
  if (error) throwUa(error);
};

export const updatePartner = async (
  id: string,
  update: Partial<Pick<PartnerProfile, "name" | "phone" | "company" | "city">>
): Promise<PartnerProfile> => {
  const { data, error } = await supabase
    .from("profiles")
    .update(update)
    .eq("id", id)
    .select("*")
    .single<PartnerProfile>();
  if (error || !data) throwUa(error);
  return data as PartnerProfile;
};
